import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { api } from '@/api/client';
import { PickerGlyph } from './pickerIcons';

/**
 * The ONLY per-connector behaviour that changes between connectors — where the
 * credentials go:
 *   - 'direct' → single step: POST /login-direct
 *   - 'otp'    → two steps: POST /login-init, then /login-otp (+ /login-resend)
 * Everything else (the shell, the product-logo header, the fields, the button)
 * is identical, which is why this is one component.
 */
export type ConnectorAuthFlow = 'direct' | 'otp';

/** Eye / eye-off glyphs for the show-password toggle (inline so no asset load). */
const EyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68M6.06 6.06C3.3 7.9 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.94-1.94" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="m2 2 20 20" />
  </svg>
);

interface Props {
  /** Product name shown beside the logo. */
  productLabel: string;
  /** Connector id — builds the auth endpoints (/api/auth/connectors/:id/…). */
  providerId: string;
  /** Connector icon (emoji or image URL) shown as the modal's product logo. */
  productIcon?: string | null;
  /** Which auth flow this connector uses — the only config that varies. */
  flow: ConnectorAuthFlow;
  onCancel(): void;
  onSuccess(): void;
}

interface InitResponse {
  requiresOtp?: boolean;
  challengeId?: string;
  // OTP-disabled tenant: /login-init links the identity immediately.
  identity?: { id: string; provider: string; email?: string };
  error?: string;
  detail?: string;
}
interface AuthResponse {
  identity?: { id: string; provider: string; email?: string };
  error?: string;
  detail?: string;
}

type Step = 'credentials' | 'otp';

function pickDetail(e: unknown): string {
  const a = e as { status?: number; body?: { detail?: string; error?: string; message?: string }; message?: string };
  return a.body?.detail || a.body?.message || a.body?.error || a.message || 'Request failed.';
}

/** Broadcast a successful link so other Brew tabs refresh their connector panels. */
function broadcastLinked(provider: string, identity: unknown): void {
  try {
    const ch = new BroadcastChannel('brew-connector-auth');
    ch.postMessage({ type: 'connector-linked', provider, identity });
    ch.close();
  } catch { /* not critical */ }
}

/**
 * ESG Caffe brand lockup — the real mug logo (public/esg-caffe-logo.png, pulled
 * from esgcaffe.ai) plus the "ESGCaffe" / "GREEN INTELLIGENCE" wordmark, which
 * their site renders as text (there's no combined-lockup asset). Shown as the
 * ESG sign-in modal's product logo in place of the generic glyph.
 */
function EsgCaffeLogo() {
  return (
    <div className="esg-caffe-logo" role="img" aria-label="ESG Caffe — Green Intelligence">
      <img className="esg-caffe-logo__mug" src="/esg-caffe-logo.png" alt="" aria-hidden="true" />
      <span className="esg-caffe-logo__word">
        <span className="esg-caffe-logo__title">
          <span className="esg-caffe-logo__esg">ESG</span>
          <span className="esg-caffe-logo__caffe">Caffe</span>
        </span>
        <span className="esg-caffe-logo__tag">GREEN INTELLIGENCE</span>
      </span>
    </div>
  );
}

/**
 * Unified connector sign-in modal — ONE file for every connector.
 *
 * The visual shell (overlay, product-logo header, credential fields, sign-in
 * button) is shared; only `flow` decides where the credentials are sent. To
 * support a new connector, give it a `flow` in ConnectorsPanel — no new file.
 * Future modal changes happen here, once.
 */
export function ConnectorAuthModal({
  productLabel, providerId, productIcon, flow, onCancel, onSuccess,
}: Props) {
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendInfo, setResendInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const otpInputRef = useRef<HTMLInputElement | null>(null);

  // ESC closes the modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent | globalThis.KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey as EventListener);
    return () => window.removeEventListener('keydown', onKey as EventListener);
  }, [onCancel]);

  // Auto-focus the OTP field when we advance to step 2.
  useEffect(() => {
    if (step === 'otp') otpInputRef.current?.focus();
  }, [step]);

  async function submitCredentials(): Promise<void> {
    if (!email.trim() || !password) {
      setErr('Email and password are required.');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      if (flow === 'direct') {
        const r = await api.post<AuthResponse>(
          `/api/auth/connectors/${providerId}/login-direct`,
          { email: email.trim(), password },
        );
        if (r.identity?.id) { broadcastLinked(providerId, r.identity); onSuccess(); return; }
        setErr(r.detail || r.error || 'Sign in failed.');
        return;
      }
      // otp flow — step 1 of 2.
      const r = await api.post<InitResponse>(
        `/api/auth/connectors/${providerId}/login-init`,
        { email: email.trim(), password },
      );
      if (r.identity?.id) { broadcastLinked(providerId, r.identity); onSuccess(); return; }
      if (!r.challengeId) { setErr(r.detail || r.error || 'Could not start sign-in.'); return; }
      setChallengeId(r.challengeId);
      setStep('otp');
    } catch (e) {
      const apiErr = e as { status?: number };
      if (apiErr.status === 401) setErr('Invalid email or password.');
      else setErr(pickDetail(e).slice(0, 160));
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp(): Promise<void> {
    if (!challengeId) {
      setErr('Missing challenge — please re-enter your password.');
      setStep('credentials');
      return;
    }
    const cleaned = otp.trim();
    if (!cleaned) { setErr('Enter the OTP from your email.'); return; }
    setErr(null);
    setBusy(true);
    try {
      const r = await api.post<AuthResponse>(
        `/api/auth/connectors/${providerId}/login-otp`,
        { challengeId, otp: cleaned },
      );
      if (r.identity?.id) { broadcastLinked(providerId, r.identity); onSuccess(); return; }
      setErr(r.detail || r.error || 'OTP verification failed.');
    } catch (e) {
      const apiErr = e as { status?: number };
      if (apiErr.status === 401) setErr('Invalid or expired OTP.');
      else setErr(pickDetail(e).slice(0, 160));
    } finally {
      setBusy(false);
    }
  }

  async function resend(): Promise<void> {
    if (!challengeId) return;
    setResendInfo(null);
    setErr(null);
    setResendBusy(true);
    try {
      await api.post(`/api/auth/connectors/${providerId}/login-resend`, { challengeId });
      setResendInfo('A new OTP has been sent.');
    } catch (e) {
      const apiErr = e as { status?: number };
      if (apiErr.status === 429) setErr('Please wait before requesting another OTP.');
      else setErr(pickDetail(e).slice(0, 160));
    } finally {
      setResendBusy(false);
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (step === 'credentials') void submitCredentials();
    else void submitOtp();
  }

  function backToCredentials(): void {
    setStep('credentials');
    setOtp('');
    setChallengeId(null);
    setErr(null);
    setResendInfo(null);
  }

  // ESG Caffe gets its own brand logo + a punchline linking to its ESG-360 page.
  const isEsg = providerId === 'esg-caffe';

  return (
    <div
      className="connector-otp-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Sign in to ${productLabel}`}
    >
      <form className="connector-otp-modal" onSubmit={handleSubmit}>
        <button
          type="button"
          aria-label="Close"
          className="connector-otp-modal__close"
          onClick={onCancel}
        >
          ✕
        </button>

        {/* The product is already chosen, so its logo (centered) is the only
            identifier — no name needed. */}
        <div className="connector-otp-modal__logo">
          {isEsg
            ? <EsgCaffeLogo />
            : <PickerGlyph icon={productIcon ?? null} label={productLabel} size={120} />}
        </div>

        {/* ESG Caffe punchline — clickable, opens the ESG-360 page. */}
        {isEsg && (
          <a
            className="connector-otp-modal__punch"
            href="https://www.esgcaffe.ai/esg-360"
            target="_blank"
            rel="noopener noreferrer"
          >
            Where Sustainability Meets Strategy.
          </a>
        )}

        {step === 'credentials' && (
          <>
            <input
              type="email"
              autoComplete="username"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="connector-otp-modal__input"
              autoFocus
            />
            <div className="connector-otp-modal__pwwrap">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="connector-otp-modal__input connector-otp-modal__input--pw"
              />
              <button
                type="button"
                className="connector-otp-modal__pwtoggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? EyeOffIcon : EyeIcon}
              </button>
            </div>
          </>
        )}

        {step === 'otp' && (
          <>
            <input
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
              className="connector-otp-modal__input connector-otp-modal__input--otp"
              maxLength={8}
            />
            <div className="connector-otp-modal__row">
              <button
                type="button"
                className="connector-otp-modal__linkbtn"
                onClick={backToCredentials}
                disabled={busy}
              >
                ← Use a different email
              </button>
              <button
                type="button"
                className="connector-otp-modal__linkbtn"
                onClick={() => void resend()}
                disabled={busy || resendBusy}
              >
                {resendBusy ? 'Sending…' : 'Resend code'}
              </button>
            </div>
            {resendInfo && <div className="connector-otp-modal__info">{resendInfo}</div>}
          </>
        )}

        {err && <div className="connector-otp-modal__err">{err}</div>}

        <button type="submit" disabled={busy} className="connector-otp-modal__submit">
          {busy
            ? (step === 'credentials' ? 'Signing in…' : 'Verifying…')
            : (step === 'credentials' ? 'Sign in' : 'Verify')}
        </button>
      </form>
    </div>
  );
}
